import { ReactNode, useMemo, useState } from 'react';
import { Group } from 'three';
import { useFilterContext, useModeContext, useTwinContext } from '@repo/front-end-state';
import { TwinEntity } from '@repo/backend-types';
import { AppMode, FilterType, SelectedNode } from '@repo/frontend-types';
import { isFloorVisible } from '@repo/front-end-utils';
import { floorGroupTransform } from '@repo/ui';
import { HeatmapFloor } from '../../behaviours/HeatmapFloor';

interface Props {
  siteID: string;
  entity: TwinEntity;
  altitude: number;
  children?: ReactNode;
}

/**
 * The empty (no-constraint) floor selection, used whenever the Equipment filter must not apply.
 * Frozen because it is shared by every `Floor` instance in the app: mutating it would leak one
 * floor's selection into every other floor's visibility test, in every mode.
 */
const NO_FLOOR_FILTER: readonly SelectedNode[] = Object.freeze([]);

/**
 * Floor Component for Sites (Structures handle their own floors)
 *
 * The Floor component offsets its children's altitude
 *
 * the coordinate systems currently in use are "COORD_LOCAL" ( Newport, Movico ).
 */

const Floor = ({ siteID, entity, altitude, children }: Props) => {
  const { filter, classificationFilters } = useFilterContext();
  const { appMode } = useModeContext();
  // Multifloor twins mount one heatmap plane per floor, inside this floor's group, scoped to this
  // floor's own subtree (structural scoping — no world-Y heuristic). A single-floor twin either has
  // no `Floor` (activated before `floor-placement`: SITE → areas) or one LONE floor; either way it is
  // not multifloor, `Site` keeps the site-level plane, and this stays dark.
  const { multifloor, hasFloorLevel } = useTwinContext();
  // A LONE floor (`floor-placement`): a one-floor twin's only floor, written so it can carry a
  // placement. Nothing can select it — it is in no Floor filter and no pill — so it is never hidden.
  const lone = hasFloorLevel && !multifloor;

  // The floor's altitude `<group>` captured into state (via callback ref) so the per-floor
  // `HeatmapFloor` mounts only once the group exists — its `root`-scoped collection then runs
  // against the right subtree on first paint (never transiently against the whole scene). The
  // splat plane derives its own bounds from the footprints collected under this group, so no
  // separate floor footprint is threaded in.
  const [floorGroup, setFloorGroup] = useState<Group | null>(null);

  // Two independent filter sources gate this floor, AND-ed inside the shared `isFloorVisible` rule:
  // the asset panel's `FilterType.ENTITY` paths, and Equipment mode's own floor selection.
  //
  // The Equipment source is MODE-GATED. `classificationFilters` is cleared on ENTERING Equipment
  // and on twin switch — NOT on leaving it (in `FilterAndModeContexts` only the classification
  // PILLS are stripped on mode exit). Without this gate, filtering to one floor in Equipment and
  // then switching to Live would leave the other floors unmounted there, with no pill on screen to
  // explain it. Same condition the classification pill effect applies, so the scene and the pills
  // are gated by one rule rather than drifting apart. The gate ALSO covers the twin-switch reset,
  // which is a passive effect on `twinBId`: a switch routes through TWIN_SELECTION first, so the
  // gate is already false for the one commit where the new twin's floors would otherwise be judged
  // against the previous twin's bids. Removing it reopens both holes, not just the mode-exit one.
  //
  // `?.floor ??` matches the two other readers of this slice (`EquipmentFilterTab`,
  // `EquipmentPanel`): a malformed context value must not throw here, because the only boundary
  // above `Floor` replaces the whole twin with a terminal error panel for the rest of the session.
  const floorFilter =
    appMode === AppMode.EQUIPMENT
      ? (classificationFilters?.floor ?? NO_FLOOR_FILTER)
      : NO_FLOOR_FILTER;

  const visible =
    lone ||
    isFloorVisible({
      // `bID ?? id` matches how `buildFloorFilterTree` keys the floor nodes the filter tree selects,
      // so a floor cannot be selectable in the tree and unmatchable in the scene.
      floorBid: entity.bID ?? entity.id,
      // MUST stay narrowed to ENTITY: `filter` also carries the DATE/TIME/PEAK pills and the
      // FLOOR/EQUIPMENT_TYPE/MANUFACTURER pills the classification effect projects into it. Passing
      // the raw array would make any of them a floor constraint and blank the whole scene.
      entityFilters: filter.filter(filterPath => filterPath.type === FilterType.ENTITY),
      siteBid: siteID,
      floorFilter,
    });

  // Hidden floors unmount their ENTIRE subtree — walls and floor surface included. That is the
  // mechanism; no per-child visibility logic exists or is needed. NOTE this unmount is now
  // user-driven and repeated (one per click in the Equipment floor tree), where it used to be rare
  // — so anything mounted under a `Floor` must dispose its own GPU resources on unmount. Several
  // existing children do not yet (`Wall`, `Hitbox`, …); tracked in `docs/specs/backlog.md`.
  const transform = useMemo(() => floorGroupTransform(entity, altitude), [entity, altitude]);

  if (!visible) return null;

  return (
    <group
      ref={setFloorGroup}
      // Names the floor for the live viewer's Edit flight, which keeps the landing floor and fades
      // the others (`spec-onboarding-persistent-scene-edit-flight`, R4.3). Inert anywhere else.
      userData={{ floorBid: entity.bID ?? entity.id }}
      // At its placement (`floor-placement`): moved and turned as aligned in the wizard; everything
      // below is drawn in the floor's own coordinates and moves with it.
      position={transform.position}
      rotation={transform.rotation}
    >
      {/* Per-floor heatmap plane (multifloor only). Scoped to this floor's group so it is
          coloured only by this floor's entities; unmounts/remounts with the floor on filter
          isolation, re-collecting on remount. Self-gates to null unless HEATMAP is active. */}
      {multifloor && floorGroup && <HeatmapFloor root={floorGroup} />}
      {children}
    </group>
  );
};

export { Floor };
